// sort-imports-ignore
import type { LucideIcon } from 'lucide-react'
import {
  Activity,
  AlarmClock,
  AlarmSmoke,
  Antenna,
  Apple,
  Armchair,
  AudioLines,
  AudioWaveform,
  Award,
  Backpack,
  Badge,
  BadgeCheck,
  BadgeDollarSign,
  BadgePercent,
  Ban,
  Bandage,
  Banknote,
  Barcode,
  Bath,
  Battery,
  BatteryCharging,
  BatteryFull,
  BatteryLow,
  BatteryMedium,
  Beer,
  Bell,
  BicepsFlexed,
  Bike,
  Binoculars,
  Bird,
  Blinds,
  Bluetooth,
  BluetoothConnected,
  Bolt,
  Bookmark,
  Box,
  Boxes,
  Brain,
  Briefcase,
  Building,
  Bus,
  Cable,
  Camera,
  Car,
  Cctv,
  Check,
  CheckCheck,
  ChefHat,
  CircleCheck,
  CircleCheckBig,
  CircleDollarSign,
  CircleHelp,
  CircleMinus,
  CirclePercent,
  CirclePlus,
  CircleX,
  CircuitBoard,
  Clock,
  Cloud,
  CloudLightning,
  CloudRain,
  CloudSnow,
  CloudSun,
  Coffee,
  Cog,
  Coins,
  Compass,
  Computer,
  Container,
  CookingPot,
  Cpu,
  CreditCard,
  Cross,
  Crosshair,
  Crown,
  CupSoda,
  Diamond,
  DollarSign,
  DoorClosed,
  DoorOpen,
  Download,
  Droplet,
  Droplets,
  Dumbbell,
  Ear,
  Earth,
  Expand,
  Eye,
  EyeOff,
  Factory,
  Fan,
  Feather,
  Filter,
  Fingerprint,
  Fish,
  Flag,
  Flame,
  Flashlight,
  Flower,
  Focus,
  Footprints,
  Fuel,
  Funnel,
  Gamepad,
  Gauge,
  Gem,
  Gift,
  GlassWater,
  Glasses,
  Globe,
  Gpu,
  Grab,
  Hammer,
  Hand,
  Handshake,
  HardHat,
  Headphones,
  Heart,
  HeartPulse,
  Heater,
  History,
  Home,
  Hotel,
  Hourglass,
  Infinity,
  Info,
  Key,
  Keyboard,
  Lamp,
  Laptop,
  Layers,
  Leaf,
  LifeBuoy,
  Lightbulb,
  Locate,
  Lock,
  LockOpen,
  Luggage,
  Magnet,
  Map,
  MapPin,
  Medal,
  Megaphone,
  MemoryStick,
  Mic,
  Microchip,
  Microwave,
  Monitor,
  MonitorSmartphone,
  Moon,
  Mountain,
  MountainSnow,
  Music,
  Nfc,
  Package,
  PackageCheck,
  PackageOpen,
  Palette,
  PawPrint,
  PencilRuler,
  Percent,
  PersonStanding,
  Phone,
  PiggyBank,
  Pill,
  Pin,
  Pipette,
  Plane,
  Plug,
  PlugZap,
  Power,
  PowerOff,
  QrCode,
  Radar,
  Radio,
  Recycle,
  Refrigerator,
  Rocket,
  Route,
  Router,
  Ruler,
  SatelliteDish,
  Scale,
  Scan,
  Scissors,
  Search,
  SearchCheck,
  Settings,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Ship,
  Shirt,
  ShoppingBag,
  ShoppingCart,
  ShowerHead,
  Signal,
  SignalHigh,
  SlidersHorizontal,
  Smartphone,
  Smile,
  Snowflake,
  Sofa,
  Sparkles,
  Speaker,
  SprayCan,
  Sprout,
  Star,
  StarHalf,
  Stars,
  Stethoscope,
  Store,
  Sun,
  SunDim,
  Sunrise,
  Sunset,
  Tablet,
  Tag,
  Tags,
  Target,
  Telescope,
  Tent,
  Thermometer,
  ThermometerSnowflake,
  ThermometerSun,
  ThumbsDown,
  ThumbsUp,
  Timer,
  TimerReset,
  Train,
  TreeDeciduous,
  TreePine,
  TrendingDown,
  TrendingUp,
  Trophy,
  Truck,
  Tv,
  Umbrella,
  Unplug,
  Usb,
  Utensils,
  Verified,
  Video,
  Volume,
  Volume2,
  VolumeOff,
  VolumeX,
  Wallet,
  Warehouse,
  WashingMachine,
  Watch,
  Waves,
  Weight,
  Wifi,
  WifiOff,
  Wind,
  Wine,
  Wrench,
  Zap,
  ZoomIn,
} from 'lucide-react'
import { LUCIDE_ICON_REGISTRY } from '@dupe/common/research-report/lucide-icons'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface EvaluationCriterion {
  label: string
  description: string
  icon?: string | null
}

interface SeoReportHowWePickedProps {
  title?: string
  subtitle?: string
  criteria: EvaluationCriterion[]
}

interface SeoReportWhoItsForProps {
  body: string
}

// ---------------------------------------------------------------------------
// Icon mapping
// ---------------------------------------------------------------------------

/**
 * Import name -> React component lookup. Must include every entry from
 * LUCIDE_ICON_REGISTRY in packages/common/research-report/lucide-icons.ts.
 * To add a new icon: add it to the registry, add the import above, and add
 * the entry here. The render map is built from the registry automatically. */
const IMPORT_MAP: Record<string, LucideIcon> = {
  Activity,
  AlarmClock,
  AlarmSmoke,
  Antenna,
  Apple,
  Armchair,
  AudioLines,
  AudioWaveform,
  Award,
  Backpack,
  Badge,
  BadgeCheck,
  BadgeDollarSign,
  BadgePercent,
  Ban,
  Bandage,
  Banknote,
  Barcode,
  Bath,
  Battery,
  BatteryCharging,
  BatteryFull,
  BatteryLow,
  BatteryMedium,
  Beer,
  Bell,
  BicepsFlexed,
  Bike,
  Binoculars,
  Bird,
  Blinds,
  Bluetooth,
  BluetoothConnected,
  Bolt,
  Bookmark,
  Box,
  Boxes,
  Brain,
  Briefcase,
  Building,
  Bus,
  Cable,
  Camera,
  Car,
  Cctv,
  Check,
  CheckCheck,
  ChefHat,
  CircleCheck,
  CircleCheckBig,
  CircleDollarSign,
  CircleHelp,
  CircleMinus,
  CirclePercent,
  CirclePlus,
  CircleX,
  CircuitBoard,
  Clock,
  Cloud,
  CloudLightning,
  CloudRain,
  CloudSnow,
  CloudSun,
  Coffee,
  Cog,
  Coins,
  Compass,
  Computer,
  Container,
  CookingPot,
  Cpu,
  CreditCard,
  Cross,
  Crosshair,
  Crown,
  CupSoda,
  Diamond,
  DollarSign,
  DoorClosed,
  DoorOpen,
  Download,
  Droplet,
  Droplets,
  Dumbbell,
  Ear,
  Earth,
  Expand,
  Eye,
  EyeOff,
  Factory,
  Fan,
  Feather,
  Filter,
  Fingerprint,
  Fish,
  Flag,
  Flame,
  Flashlight,
  Flower,
  Focus,
  Footprints,
  Fuel,
  Funnel,
  Gamepad,
  Gauge,
  Gem,
  Gift,
  GlassWater,
  Glasses,
  Globe,
  Gpu,
  Grab,
  Hammer,
  Hand,
  Handshake,
  HardHat,
  Headphones,
  Heart,
  HeartPulse,
  Heater,
  History,
  Home,
  Hotel,
  Hourglass,
  Infinity,
  Info,
  Key,
  Keyboard,
  Lamp,
  Laptop,
  Layers,
  Leaf,
  LifeBuoy,
  Lightbulb,
  Locate,
  Lock,
  LockOpen,
  Luggage,
  Magnet,
  Map,
  MapPin,
  Medal,
  Megaphone,
  MemoryStick,
  Mic,
  Microchip,
  Microwave,
  Monitor,
  MonitorSmartphone,
  Moon,
  Mountain,
  MountainSnow,
  Music,
  Nfc,
  Package,
  PackageCheck,
  PackageOpen,
  Palette,
  PawPrint,
  PencilRuler,
  Percent,
  PersonStanding,
  Phone,
  PiggyBank,
  Pill,
  Pin,
  Pipette,
  Plane,
  Plug,
  PlugZap,
  Power,
  PowerOff,
  QrCode,
  Radar,
  Radio,
  Recycle,
  Refrigerator,
  Rocket,
  Route,
  Router,
  Ruler,
  SatelliteDish,
  Scale,
  Scan,
  Scissors,
  Search,
  SearchCheck,
  Settings,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Ship,
  Shirt,
  ShoppingBag,
  ShoppingCart,
  ShowerHead,
  Signal,
  SignalHigh,
  SlidersHorizontal,
  Smartphone,
  Smile,
  Snowflake,
  Sofa,
  Sparkles,
  Speaker,
  SprayCan,
  Sprout,
  Star,
  StarHalf,
  Stars,
  Stethoscope,
  Store,
  Sun,
  SunDim,
  Sunrise,
  Sunset,
  Tablet,
  Tag,
  Tags,
  Target,
  Telescope,
  Tent,
  Thermometer,
  ThermometerSnowflake,
  ThermometerSun,
  ThumbsDown,
  ThumbsUp,
  Timer,
  TimerReset,
  Train,
  TreeDeciduous,
  TreePine,
  TrendingDown,
  TrendingUp,
  Trophy,
  Truck,
  Tv,
  Umbrella,
  Unplug,
  Usb,
  Utensils,
  Verified,
  Video,
  Volume,
  Volume2,
  VolumeOff,
  VolumeX,
  Wallet,
  Warehouse,
  WashingMachine,
  Watch,
  Waves,
  Weight,
  Wifi,
  WifiOff,
  Wind,
  Wine,
  Wrench,
  Zap,
  ZoomIn,
}

/** kebab-case icon name -> React component, built from the shared registry */
const LUCIDE_RENDER_MAP: Record<string, LucideIcon> = Object.fromEntries(
  LUCIDE_ICON_REGISTRY.map((entry) => [
    entry.name,
    IMPORT_MAP[entry.importName],
  ]).filter(([, component]) => component != null)
)

function resolveCriterionIcon(iconName?: string | null): LucideIcon {
  if (iconName) {
    const resolved = LUCIDE_RENDER_MAP[iconName.toLowerCase()]
    if (resolved) return resolved
  }
  return Sparkles
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function CriterionItem({ criterion }: { criterion: EvaluationCriterion }) {
  const IconComponent = resolveCriterionIcon(criterion.icon)

  return (
    <div className='flex gap-3'>
      <div className='flex h-[26px] w-[26px] flex-none items-center justify-center rounded-full bg-info-500 p-1.5 md:h-8 md:w-8'>
        <IconComponent
          className='h-4 w-4 text-white md:h-5 md:w-5'
          strokeWidth={1.5}
        />
      </div>
      <div className='flex flex-col gap-1 md:pt-px'>
        <h3 className='text-base font-semibold leading-[26px] text-foreground md:text-xl md:leading-[30px]'>
          {criterion.label}
        </h3>
        <p className='text-base font-normal leading-6 text-foreground-secondary'>
          {criterion.description}
        </p>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function SeoReportHowWePicked({
  title = 'How we picked',
  subtitle = 'This is the evaluation criteria and key considerations our agents used when choosing products for this report.',
  criteria,
}: SeoReportHowWePickedProps) {
  return (
    <section className='flex flex-col gap-9'>
      {/* Header */}
      <div className='flex flex-col gap-3'>
        <h2 className='font-display text-2xl font-bold leading-8 text-foreground'>
          {title}
        </h2>
        {subtitle && (
          <p className='text-sm font-normal leading-5 text-foreground-secondary'>
            {subtitle}
          </p>
        )}
      </div>

      {/* Evaluation criteria grid */}
      {criteria.length > 0 && (
        <div className='grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-x-4'>
          {criteria.map((criterion) => (
            <CriterionItem key={criterion.label} criterion={criterion} />
          ))}
        </div>
      )}
    </section>
  )
}

export function SeoReportWhoItsFor({ body }: SeoReportWhoItsForProps) {
  const paragraphs = body
    .split(/\n\n+/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)

  if (paragraphs.length === 0) return null

  return (
    <section className='flex flex-col gap-3'>
      <h2 className='font-display text-2xl font-bold leading-8 text-foreground'>
        Who this is for
      </h2>
      <div className='flex flex-col gap-4 text-base font-normal leading-7 text-foreground md:text-lg'>
        {paragraphs.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
      </div>
    </section>
  )
}
